import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import type { DeskListFilterResetActions } from "./use-desk-list-filter-reset";

export type DeskFilterResetState = DeskListFilterResetActions;

type DataTableChromeContextValue = {
  /** Header mount for Refresh + ⋯ (+ filter reset via portal) */
  host: HTMLElement | null;
  setHost: (el: HTMLElement | null) => void;
  /** Legend-row mount for table/stacked layout toggle */
  layoutHost: HTMLElement | null;
  setLayoutHost: (el: HTMLElement | null) => void;
  filterReset: DeskFilterResetState | null;
  setFilterReset: (next: DeskFilterResetState | null) => void;
};

const DataTableChromeContext =
  createContext<DataTableChromeContextValue | null>(null);

/** Wrap a Desk list so DataTable can mount chrome into header + legend row. */
export function DataTableChrome(props: { children: ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [layoutHost, setLayoutHost] = useState<HTMLElement | null>(null);
  const [filterReset, setFilterReset] = useState<DeskFilterResetState | null>(
    null,
  );
  const value = useMemo(
    () => ({
      host,
      setHost,
      layoutHost,
      setLayoutHost,
      filterReset,
      setFilterReset,
    }),
    [host, layoutHost, filterReset],
  );
  return (
    <DataTableChromeContext.Provider value={value}>
      {props.children}
    </DataTableChromeContext.Provider>
  );
}

/** Mount point for the DataTable overflow control (Refresh + ⋯). */
export function DataTableChromeHost(props: { className?: string }) {
  const ctx = useContext(DataTableChromeContext);
  if (!ctx) return null;
  return (
    <div
      ref={ctx.setHost}
      className={cn("flex items-center", props.className)}
    />
  );
}

/**
 * Mount point for table ↔ stacked layout toggle.
 * Place on the DocListLegend row (or any list chrome strip above the table).
 */
export function DataTableLayoutHost(props: { className?: string }) {
  const ctx = useContext(DataTableChromeContext);
  if (!ctx) return null;
  return (
    <div
      ref={ctx.setLayoutHost}
      className={cn("ml-auto flex shrink-0 items-center", props.className)}
    />
  );
}

/**
 * Registers reset-filters into DataTable chrome (header quick actions).
 * Render once inside `DataTableChrome`; renders nothing itself.
 */
export function DeskListFilterReset(props: DeskListFilterResetActions) {
  const setFilterReset = useContext(DataTableChromeContext)?.setFilterReset;
  const {
    canResetToDefault,
    canClearAll,
    onResetToDefault,
    onClearAll,
  } = props;
  useEffect(() => {
    if (!setFilterReset) return;
    setFilterReset({
      canResetToDefault,
      canClearAll,
      onResetToDefault,
      onClearAll,
    });
    return () => setFilterReset(null);
  }, [
    setFilterReset,
    canResetToDefault,
    canClearAll,
    onResetToDefault,
    onClearAll,
  ]);
  return null;
}

export function useDataTableChromeHost(): HTMLElement | null {
  return useContext(DataTableChromeContext)?.host ?? null;
}

export function useDataTableLayoutHost(): HTMLElement | null {
  return useContext(DataTableChromeContext)?.layoutHost ?? null;
}

export function useDeskFilterReset(): DeskFilterResetState | null {
  return useContext(DataTableChromeContext)?.filterReset ?? null;
}
